<template>
  <div class="column-index">
    <div class="index-container">
      <div v-if="isLoading" class="d-flex justify-content-center align-items-center py-4 h-100">
        <v-progress-circular color="primary" indeterminate></v-progress-circular>
      </div>

      <template v-else>
        <div class="d-flex">
          <h4 class="ma-0" style="font-size: 16px;"><b>Rearrange column</b> <span class="badge badge-primary ml-1"
              style="line-height: inherit;">{{ selectedColumns.length }}</span></h4>
        </div>
        <div class="mt-1">
          <p class="ma-0" :class="{ 'line-clamp': !expanded }">The order of the column list, from top to bottom, matches
            the left-to-right order in the report preview. Adjust the order by dragging the cards up or down, then click
            the update button below to save your changes.</p>
        </div>
        <div class="d-flex justify-content-center align-items-center">
          <v-divider color="#D7D7D7" />
          <div class="d-flex justify-content-center align-items-center ml-2" @click="expanded = !expanded"
            style="cursor: pointer;">
            {{ expanded ? 'less' : 'more' }}
            <v-icon small color="#3F3F3F" class="ml-1">{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
        </div>
        <div style="overflow: auto;">
          <draggable v-model="selectedColumns" group="column-index" @start="drag = true" @end="updateColumnPositions"
            handle=".column-drag-handle" class="d-flex flex-column list-group" ghost-class="moving-class"
            style="gap: 8px;">
            <transition-group type="transition" name="flip-list">
              <div v-for="(column, index) in selectedColumns" :key="index" class="column-item list-group-item">
                <v-icon small class="column-drag-handle" style="cursor: grab;">mdi-drag-vertical</v-icon>
                <div v-if="column.group === 'Questionnaire::Template'" class="d-flex flex-column ml-2">
                  <b>#{{ column.columnName }} {{ column.header_name }}</b>
                  <div v-for="(item, index) in column.data" :key="index" class="d-flex flex-column ml-2"
                    style="gap: 4px;">
                    <b style="font-size: 12px;">{{ item.header_name }}</b>
                    <i style="color: #848484; font-size: 12px;">{{ item.original_header_name }}</i>
                  </div>
                </div>
                <div v-else class="d-flex flex-column ml-2">
                  <b>{{ column.header_name }}</b>
                  <i style="color: #848484; font-size: 12px;">{{ column.original_header_name }}</i>
                </div>
              </div>
            </transition-group>
          </draggable>
        </div>
      </template>
    </div>
  </div>

</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import draggable from 'vuedraggable'


export default {
  components: {
    draggable,
  },
  props: {
    drawerShow: Boolean,
  },
  computed: {
    ...mapGetters({
      isLoading: 'reportTemplates/isLoading',
    }),
    selectedColumns: {
      get() {
        let cols = this.$store.getters['reportTemplates/selectedColumns'] || [];
        const reportType = this.$store.getters['reportTemplates/reportTemplate']?.type;
        if (reportType === 'Report::Join') {
          cols = cols.filter(col => col.group !== 'case_business_unit');
        }
        // Add "(Client)" suffix for client BU/Department columns in Case/Join context.
        return cols.map(col => {
          const columnKey = (col.column_name ?? col.columnName ?? '').toString();
          const headerName = (col.header_name ?? '').toString();
          const originalHeaderName = (col.original_header_name ?? '').toString();
          const isClientBusinessDepartmentColumn =
            columnKey.includes('client_business_unit') ||
            columnKey.includes('client_department') ||
            columnKey === 'primary_client_business_unit_object_name' ||
            columnKey === 'primary_client_department_object_name' ||
            col.group === 'client_business_unit';

          if (isClientBusinessDepartmentColumn) {
            return {
              ...col,
              header_name: headerName.includes('(Client)') ? headerName : `${headerName} (Client)`,
              original_header_name: originalHeaderName.includes('(Client)') ? originalHeaderName : `${originalHeaderName} (Client)`
            };
          }
          return col;
        });
      },
      set(newSelectedColumns) {
        this.$store.commit('reportTemplates/setSelectedColumns', newSelectedColumns)
      }
    },
  },
  data() {
    return {
      expanded: false,
    }
  },
  methods: {
    updateColumnPositions() {
      const updatedColumns = this.selectedColumns.map((column, index) => ({
        ...column,
        position: index + 1,
      }));
      this.selectedColumns = updatedColumns;
      this.$store.commit('reportTemplates/setUnsavedChanges', true);
    },
  },
  mounted() {
    setTimeout(() => {

    }, 500);
  },
  beforeDestroy() {

  },
  watch: {
    drawerShow(newVal) {
      this.localDrawerShow = newVal;
    },
  },
}
</script>

<style lang="scss" scoped>
.column-index {
  color: #3F3F3F;
  padding: 16px 0 0 16px;

  .index-container {
    background-color: #FFF;
    width: 290px;
    padding: 16px;
    border: 1px solid #D7D7D7;
    border-radius: 8px;
    height: calc(100vh - 152px);
    display: flex;
    flex-direction: column;
  }

  .column-item {
    width: 100%;
    padding: 8px;
    display: flex;
    align-items: center;
    border: 1px solid #D7D7D7;
    border-radius: 4px;
  }

  .list-group span {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .line-clamp {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

}

::v-deep.moving-class {
  position: relative;
}

::v-deep.moving-class::before {
  content: '';
  position: absolute;
  top: -1px;
  left: 0;
  height: 5px;
  width: 100%;
  border-radius: 5px;
  border-top: 3px solid #3ea4ffe6;
}
</style>
